import { DotpadSDK } from "@/util/dotpad/DotpadSDK.js";
import { FabricImage, Group, Rect, Line, Canvas, StaticCanvas } from "fabric";
import { getLayerColor, resolvePinColorIndexes } from "@/util/canvas/layerState";

const dotpadSDK = new DotpadSDK()
const NUMBER_OF_HORIZONTAL_PINS = 60
const NUMBER_OF_VERTICAL_PINS = 40

/**
 핀마다 칠할 색 번호. 그리는 함수의 params.pinColors 로 넘긴다(DCW-1944·1945·1946).
 색 레이어(또는 앱 색 graphic.colors)가 없는 페이지는 null 이라 지금처럼 검정으로 그린다.
 - param item: 페이지(Page 또는 파일 JSON 의 items[i])
 - param pixelArr: 그릴 켜진 핀(getPixelArray 결과 등)
 - param options: {includeLayerIds} 일부 레이어만 그릴 때
 - returns {Int8Array|null}
 */
function getPinColors(item, pixelArr, options = {}) {
    return resolvePinColorIndexes(item, pixelArr, {fallbackSurface: item?.dotPadInfo, ...options})
}

// 핀 칠할 색. pinColors 가 없으면 검정이다.
function pinFill(pinColors, index) {
    return pinColors ? getLayerColor(pinColors[index]).cssColor : 'black'
}

/**
 [Dot Canvas - 발표모드, Doc Cloud - Card 썸네일]
 캔버스에 올바르게 핀 세팅 하도록 캔버스 픽셀 크기 계산
 - param item
 - returns {*[]}
 */
function getPixelArray(item) {
    dotpadSDK.setDotPadSDKInfo(item.dotPadInfo)
    const graphicData = item.graphic.data               // 600자 Hex String
    const byteArr = dotpadSDK.hexToBytes(graphicData)   // 300 Bytes Array
    return _calculatePixelArray(byteArr, item.dotPadInfo)
}

// 캔버스 생성
function createCanvas(canvasId, canvasWidth, canvasHeight) {
    const canvas = new Canvas(canvasId)
    canvas.setDimensions({ width: canvasWidth, height: canvasHeight })

    return canvas
}

/**
 * 확대해도 뿌옇지 않게, 화면 크기(cssWidth×cssHeight)는 그대로 두고 내부 비트맵만 배율만큼 키운다.
 *
 * 발표모드는 뷰포트 크기로 한 번 그린 캔버스에 CSS zoom 을 얹어 확대한다. 그러면 60×40 을
 * 그린 비트맵을 늘려 보여 주는 것이라 400% 에서 핀 가장자리가 번진다. 여기서는 백스토어를
 * zoom 배로 잡고 fabric 의 뷰포트 배율도 같이 올려, CSS zoom 이 보여 주는 픽셀 수만큼 실제로
 * 그린다. 화면 크기와 스크롤·좌표 계산(clientWidth 기준)은 바뀌지 않는다.
 *
 * 너무 큰 비트맵은 브라우저가 조용히 버리거나 메모리를 크게 먹으므로 한 변을 maxSide 로 막는다.
 * (레티나 2배 · 1500px 뷰포트 · 400% 면 12000px 이 된다)
 *
 * @returns {number} 실제로 적용한 배율
 */
function applyCrispZoom(canvas, zoom, cssWidth, cssHeight, {maxSide = 8192} = {}) {
    if (!canvas || !(cssWidth > 0) || !(cssHeight > 0)) return 1

    const retina = canvas.getRetinaScaling?.() || 1
    const longestSide = Math.max(cssWidth, cssHeight) * retina
    const wanted = Math.max(1, Number(zoom) || 1)
    const scale = Math.max(1, Math.min(wanted, maxSide / longestSide))

    canvas.setDimensions({width: cssWidth * scale, height: cssHeight * scale}, {backstoreOnly: true})
    canvas.setDimensions({width: cssWidth, height: cssHeight}, {cssOnly: true})
    canvas.setZoom(scale)
    canvas.requestRenderAll()

    return scale
}

function createStaticCanvas(canvasId, canvasWidth, canvasHeight) {
    const staticCanvas = new StaticCanvas(canvasId)
    staticCanvas.setDimensions({ width: canvasWidth, height: canvasHeight })

    return staticCanvas
}

// 캔버스에 그리드 그리기
function drawGridOnTheCanvas(canvasWidth, canvasHeight) {
    const gridLines = [];
    const gridOptions = {selectable: false, stroke: '#cccccc'};

    for (let row = 0; row < NUMBER_OF_VERTICAL_PINS; row++) {
        const y = (row / NUMBER_OF_VERTICAL_PINS) * canvasHeight;
        gridLines.push(new Line([0, y, canvasWidth, y], gridOptions));
    }

    for (let column = 0; column < NUMBER_OF_HORIZONTAL_PINS; column++) {
        const x = (column / NUMBER_OF_HORIZONTAL_PINS) * canvasWidth;
        gridLines.push(new Line([x, 0, x, canvasHeight], gridOptions));
    }

    return new Group(gridLines, {
        selectable: false,
        evented: false,
    });
}

/**
 캔버스에 그리드 그리기
 - params{dotPadInfo, pixelArr, canvasWidth, canvasHeight}
 - returns {Group}
 */
function drawGridOnTheCanvasForChangeTest(params) {
    const dotPadInfo = params.dotPadInfo
    const horizontalPins = dotPadInfo ? dotPadInfo.width : NUMBER_OF_HORIZONTAL_PINS      //HORIZONTAL_PINS = 768(96), 320(60)
    const verticalPins = dotPadInfo ? dotPadInfo.height : NUMBER_OF_VERTICAL_PINS         //VERTICAL_PINS = 768(64), 320(40)
    const canvasHeight = params.canvasHeight
    const canvasWidth = params.canvasWidth
    const gridLines = []
    const gridOptions = {selectable: false, stroke: '#cccccc'}
    for (let row = 0; row < verticalPins; row++) {
        const y = (row / verticalPins) * canvasHeight
        gridLines.push(new Line([0, y, canvasWidth, y], gridOptions))
    }

    for (let column = 0; column < horizontalPins; column++) {
        const x = (column / horizontalPins) * canvasWidth
        gridLines.push(new Line([x, 0, x, canvasHeight], gridOptions))
    }

    return new Group(gridLines, {
        selectable: false,
        evented: false,
    })
}

// 캔버스에 촉각 정보 그리기. pinColors(getPinColors)를 주면 핀을 그 레이어 색으로 칠한다.
function drawTactileDataOnTheCanvas(pixelArr, canvasWidth, canvasHeight, pinColors = null) {
    const pinW = canvasWidth / NUMBER_OF_HORIZONTAL_PINS;
    const pinH = canvasHeight / NUMBER_OF_VERTICAL_PINS;
    const array = [];
    let index = 0;

    for (let i = 0; i < NUMBER_OF_VERTICAL_PINS; i++) {
        for (let j = 0; j < NUMBER_OF_HORIZONTAL_PINS; j++) {
            if (pixelArr[index]) {
                const pin = new Rect({
                    fill: pinFill(pinColors, index),
                    left: pinW * j,
                    top: pinH * i,
                    width: pinW,
                    height: pinH,
                    hasBorders: false,
                    hasControls: false,
                    hasRotatingPoint: false,
                    lockMovementX: true,
                    lockMovementY: true,
                    selectable: false,
                    hoverCursor: "default",
                });
                array.push(pin);
            }
            index++;
        }
    }

    return new Group(array);
}

/**
   캔버스에 촉각 정보 그리기
 - params{dotPadInfo, pixelArr, canvasWidth, canvasHeight, pinColors?}
 - returns {Group}
 */
function drawTactileDataOnTheCanvasForChangeTest(params) {
    const dotPadInfo = params.dotPadInfo;
    const pixelArr = params.pixelArr;
    const pinColors = params.pinColors || null;
    const horizontalPins = dotPadInfo ? dotPadInfo.width : NUMBER_OF_HORIZONTAL_PINS;        //HORIZONTAL_PINS = 768(90), 320(60);
    const verticalPins = dotPadInfo ? dotPadInfo.height : NUMBER_OF_VERTICAL_PINS;         //VERTICAL_PINS = 768(64), 320(40);
    const pinW = params.canvasWidth / horizontalPins;
    const pinH = params.canvasHeight / verticalPins;
    const array = [];
    let index = 0;

    for (let i = 0; i < verticalPins; i++) {
        for (let j = 0; j < horizontalPins; j++) {
            if (pixelArr[index]) {
                const pin = new Rect({
                    fill: pinFill(pinColors, index),
                    left: pinW * j,
                    top: pinH * i,
                    width: pinW,
                    height: pinH,
                    hasBorders: false,
                    hasControls: false,
                    hasRotatingPoint: false,
                    lockMovementX: true,
                    lockMovementY: true,
                    selectable: false,
                    hoverCursor: "default",
                });
                array.push(pin);
            }
            index++;
        }
    }

    return new Group(array);
}

/**
 촉각 정보를 fabric 오브젝트 1개로 그리기
 drawTactileDataOnTheCanvasForChangeTest 는 켜진 핀 1개당 Rect 를 만들기 때문에
 페이지당 최대 (width * height) 개의 오브젝트가 생긴다.
 썸네일처럼 상호작용이 필요 없는 곳에서는 오프스크린 캔버스에 한 번 그려 이미지 1개로 올린다.
 - params{dotPadInfo, pixelArr, canvasWidth, canvasHeight, pinColors?}
 - returns {FabricImage}
 */
function drawTactileDataAsImage(params) {
    const dotPadInfo = params.dotPadInfo
    const pixelArr = params.pixelArr
    const pinColors = params.pinColors || null
    const horizontalPins = dotPadInfo ? dotPadInfo.width : NUMBER_OF_HORIZONTAL_PINS
    const verticalPins = dotPadInfo ? dotPadInfo.height : NUMBER_OF_VERTICAL_PINS
    const canvasWidth = params.canvasWidth
    const canvasHeight = params.canvasHeight
    const pinW = canvasWidth / horizontalPins
    const pinH = canvasHeight / verticalPins

    // fabric 은 retina 대응으로 캔버스를 devicePixelRatio 배로 렌더한다.
    // 오프스크린을 CSS 픽셀 크기로만 그리면 고DPI 화면에서 흐려지므로 같은 배율로 그린다.
    const pixelRatio = window.devicePixelRatio || 1
    const bitmapWidth = Math.max(1, Math.round(canvasWidth * pixelRatio))
    const bitmapHeight = Math.max(1, Math.round(canvasHeight * pixelRatio))

    const offscreen = document.createElement("canvas")
    offscreen.width = bitmapWidth
    offscreen.height = bitmapHeight

    const ctx = offscreen.getContext("2d")
    ctx.scale(bitmapWidth / canvasWidth, bitmapHeight / canvasHeight)
    ctx.fillStyle = 'black'

    let index = 0
    for (let i = 0; i < verticalPins; i++) {
        for (let j = 0; j < horizontalPins; j++) {
            if (pixelArr[index]) {
                if (pinColors) ctx.fillStyle = pinFill(pinColors, index)
                ctx.fillRect(pinW * j, pinH * i, pinW, pinH)
            }
            index++
        }
    }

    return new FabricImage(offscreen, {
        left: 0,
        top: 0,
        // 비트맵을 원래 표시 크기로 되돌린다.
        scaleX: canvasWidth / bitmapWidth,
        scaleY: canvasHeight / bitmapHeight,
        hasBorders: false,
        hasControls: false,
        lockMovementX: true,
        lockMovementY: true,
        selectable: false,
        evented: false,
        hoverCursor: "default",
    })
}

/*
 핀을 기기 픽셀 비트맵에 칠한다(width·height 는 기기 픽셀).
 핀 한 칸이 2 기기 픽셀 이상이면 경계를 정수 픽셀에 맞춰 가장자리가 번지지 않게 한다. 그보다 작으면(목록 보기의
 작은 썸네일) 소수 위치 그대로 섞어 그린다 - 정수에 맞추면 폭 0 이 되어 사라지는 핀이 생긴다.
 */
function paintPins(ctx, {pixelArr, pinColors, columns, rows, width, height}) {
    const pitchX = width / columns
    const pitchY = height / rows
    const snap = pitchX >= 2 && pitchY >= 2
    ctx.fillStyle = 'black'
    let index = 0
    for (let row = 0; row < rows; row++) {
        for (let column = 0; column < columns; column++, index++) {
            if (!pixelArr[index]) continue
            if (pinColors) ctx.fillStyle = pinFill(pinColors, index)
            if (snap) {
                const x = Math.round(column * pitchX)
                const y = Math.round(row * pitchY)
                ctx.fillRect(x, y, Math.round((column + 1) * pitchX) - x, Math.round((row + 1) * pitchY) - y)
            } else {
                ctx.fillRect(column * pitchX, row * pitchY, pitchX, pitchY)
            }
        }
    }
}

/**
 클라우드 썸네일을 화면에 보이는 크기 그대로 선명하게 그린다(DCW-1946).
 예전에는 작은 캔버스(카드 99×99, 목록 50×30)에 그린 뒤 CSS 가 늘려(카드 약 258×171, 목록 43×28) 핀이 뭉개졌다.
 - 크기: 캔버스 요소의 테두리 안 크기(clientWidth·clientHeight). 아직 배치되지 않아 0 이면 fallback 크기.
 - 비트맵은 그 크기 × 기기 픽셀 배율로 한 장 그리고, fabric 이 1:1 로 옮긴다(핀마다 개체를 만들지 않는다).
 - params {canvasId, pixelArr, pinColors?, dotPadInfo, fallbackWidth, fallbackHeight}
 - returns {StaticCanvas}
 */
function drawSharpThumbnail(params) {
    const element = document.getElementById(params.canvasId)
    const width = Math.round(element?.clientWidth || 0) || params.fallbackWidth
    const height = Math.round(element?.clientHeight || 0) || params.fallbackHeight
    const canvas = createStaticCanvas(params.canvasId, width, height)
    const ratio = canvas.getRetinaScaling?.() || window.devicePixelRatio || 1
    const bitmap = document.createElement("canvas")
    bitmap.width = Math.max(1, Math.floor(width * ratio))
    bitmap.height = Math.max(1, Math.floor(height * ratio))
    paintPins(bitmap.getContext("2d"), {
        pixelArr: params.pixelArr,
        pinColors: params.pinColors || null,
        columns: params.dotPadInfo?.width || NUMBER_OF_HORIZONTAL_PINS,
        rows: params.dotPadInfo?.height || NUMBER_OF_VERTICAL_PINS,
        width: bitmap.width,
        height: bitmap.height,
    })
    canvas.add(new FabricImage(bitmap, {
        // fabric 7 기본 기준점은 가운데다. 앱은 main.js 에서 왼쪽 위로 바꾸지만 여기서도 정해 둔다.
        originX: "left",
        originY: "top",
        left: 0,
        top: 0,
        // 비트맵을 표시 크기로 되돌린다. fabric 이 기기 픽셀 배율로 다시 키워 1:1 이 된다.
        scaleX: width / bitmap.width,
        scaleY: height / bitmap.height,
        // 캐시 캔버스를 거치면 한 번 더 늘였다 줄여 흐려진다.
        objectCaching: false,
        hasBorders: false,
        hasControls: false,
        selectable: false,
        evented: false,
        hoverCursor: "default",
    }))
    return canvas
}

function getVoxel() {
  return {
    NUMBER_OF_HORIZONTAL_PINS,
    NUMBER_OF_VERTICAL_PINS,
  }
}

// 텍타일 데이터의 좌표값으로 인덱스를 구하는 함수
function calculateIndexFromRect(rect, canvasWidth, canvasHeight) {
  const pinW = canvasWidth / NUMBER_OF_HORIZONTAL_PINS;
  const pinH = canvasHeight / NUMBER_OF_VERTICAL_PINS;

  // rect의 좌표를 가져옴
  const left = rect.left;
  const top = rect.top;

  // 해당 좌표의 행과 열을 계산
  const row = Math.floor(top / pinH);
  const col = Math.floor(left / pinW);

  // 현재 인덱스 번호 계산
  const index = row * NUMBER_OF_HORIZONTAL_PINS + col;

  return index;
}

function _calculatePixelArray(byteArr, dotPadInfo) {
    const result = [];
    for (let i = 0; i < byteArr.length; i++) {
        const start_index = Math.floor(i / dotPadInfo.halfWidth) * dotPadInfo.width * 4 + (i % dotPadInfo.halfWidth) * 2;
        result[start_index] = !!(byteArr[i] & (0x01 << 0));
        result[start_index + dotPadInfo.width] = !!(byteArr[i] & (0x01 << 1));
        result[start_index + dotPadInfo.doubleWidth] = !!(byteArr[i] & (0x01 << 2));
        result[start_index + dotPadInfo.tripleWidth] = !!(byteArr[i] & (0x01 << 3));
        result[start_index + 1] = !!(byteArr[i] & (0x01 << 4));
        result[start_index + dotPadInfo.width + 1] = !!(byteArr[i] & (0x01 << 5));
        result[start_index + dotPadInfo.doubleWidth + 1] = !!(byteArr[i] & (0x01 << 6));
        result[start_index + dotPadInfo.tripleWidth + 1] = !!(byteArr[i] & (0x01 << 7));
    }
    return result;
}

export default {
    createCanvas,
    createStaticCanvas,
    applyCrispZoom,
    drawGridOnTheCanvas,
    drawGridOnTheCanvasForChangeTest,
    drawSharpThumbnail,
    drawTactileDataAsImage,
    drawTactileDataOnTheCanvas,
    drawTactileDataOnTheCanvasForChangeTest,
    getPinColors,
    getPixelArray,
    getVoxel,
}
